import { z, ZodErrorMap, ZodIssueCode } from "zod";
import { TENANT_ID_PATTERN, TENANT_ID_PATTERN_MESSAGE } from "@/src/lib/tenantSlug";

export const polishErrorMap: ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
  case ZodIssueCode.invalid_type:
    if (issue.expected === "string") {
      return { message: "To pole jest wymagane" };
    } else if (issue.expected === "number") {
      return { message: "Oczekiwano liczby" };
    }
    // Dodaj inne typy, które chcesz obsłużyć
    return { message: `Oczekiwano typu ${issue.expected}` };
  case ZodIssueCode.invalid_literal:
    return { message: `Wartość musi być równa ${issue.expected}` };
  case ZodIssueCode.unrecognized_keys:
    return { message: `Niezrozumiane klucze w obiekcie: ${issue.keys.join(", ")}` };
  case ZodIssueCode.invalid_union:
    return { message: "Niepoprawna wartość" };
  case ZodIssueCode.invalid_enum_value:
    return { message: `Wartość musi być jedną z: ${issue.options.join(", ")}` };
  case ZodIssueCode.invalid_arguments:
    return { message: "Niepoprawne argumenty funkcji" };
  case ZodIssueCode.invalid_return_type:
    return { message: "Niepoprawny typ zwrotu funkcji" };
  case ZodIssueCode.invalid_date:
    return { message: "Niepoprawna data" };
  case ZodIssueCode.invalid_string:
    if (issue.validation === "email") {
      return { message: "Niepoprawny format adresu email" };
    } else if (issue.validation === "url") {
      return { message: "Niepoprawny format URL" };
    } else if (issue.validation === "regex") {
      return { message: "Wartość nie pasuje do wzorca" };
    } else if (issue.validation === "uuid") {
      return { message: "Niepoprawny format UUID" };
    } else if (issue.validation === "cuid") {
      return { message: "Niepoprawny format CUID" };
    }
    return { message: `Niepoprawny format łańcucha znaków: ${issue.validation}` };
  case ZodIssueCode.too_small:
    if (issue.type === "string") {
      return { message: `Łańcuch znaków jest za krótki. Minimalna długość to ${issue.minimum}` };
    } else if (issue.type === "number") {
      return { message: `Liczba jest za mała. Minimalna wartość to ${issue.minimum}` };
    }
    return { message: `Wartość jest za mała. Minimalna wartość to ${issue.minimum}` };
  case ZodIssueCode.too_big:
    if (issue.type === "string") {
      return { message: `Łańcuch znaków jest za długi. Maksymalna długość to ${issue.maximum}` };
    } else if (issue.type === "number") {
      return { message: `Liczba jest za duża. Maksymalna wartość to ${issue.maximum}` };
    }
    return { message: `Wartość jest za duża. Maksymalna wartość to ${issue.maximum}` };
  case ZodIssueCode.custom:
    return { message: "Niepoprawna wartość" };
  case ZodIssueCode.invalid_intersection_types:
    return { message: "Typy przecięcia są niepoprawne" };
  case ZodIssueCode.not_multiple_of:
    return { message: `Wartość musi być wielokrotnością ${issue.multipleOf}` };
  default:
    return { message: ctx.defaultError };
  }
};

export const STAKEHOLDER_TYPE = ["Pełnomocnik", "Pełnomocnik do doręczeń"] as const;
export type StakholderType = (typeof STAKEHOLDER_TYPE)[number];

export const YES_NO_OPTIONS = ["Tak", "Nie"] as const;
export type YesNoOptions = (typeof YES_NO_OPTIONS)[number];

export const ATTACHMENT_TYPES = [
  "powerOfAttorney",
  "stampDutyProof",
  "graphicBoundary",
  "other",
] as const;
export type AttachmentType = (typeof ATTACHMENT_TYPES)[number];


const contacDetailsSchema = z.object({
  country: z.string(),
  voivodeship: z.string(),
  district: z.string(),
  tenant: z.string(),
  street: z.string(),
  houseNumber: z.string(),
  apartmentNumber: z.string().nullish(),
  town: z.string(),
  postalCode: z.string().regex(/^\d{2}-\d{3}$/, { message: "Niepoprawny format kodu pocztowego" }), // Polish postal code format
});

const nullableContacDetailsSchema = z.object({
  country: z.string().nullish(),
  voivodeship: z.string().nullish(),
  district: z.string().nullish(),
  tenant: z.string().nullish(),
  street: z.string().nullish(),
  houseNumber: z.string().nullish(),
  apartmentNumber: z.string().optional(),
  town: z.string().nullish(),
  postalCode: z
    .string()
    .regex(/^\d{2}-\d{3}$/, { message: "Niepoprawny format kodu pocztowego" })
    .optional()
    .nullable(), // Polish postal code format
});

const geometrySchema = z.object({
  type: z.string(),
  bbox: z.number().array(),
  center: z.object({
    lng: z.number(),
    lat: z.number()
  }),
  coordinates: z.array(z.unknown())
});
const stakeholderSchema = contacDetailsSchema.extend({
  name: z.string(),
  email: z.string().email().optional(),
  phone: z
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/, { message: "Niepoprawny nr telefonu" })
    .optional(),
  epuapAdress: z.string().optional(),
  parcels: z.object({
    id: z.string(),
    geometry: geometrySchema.array()
  }).array().optional()
});
const nullableStakeholderSchema = nullableContacDetailsSchema.extend({
  name: z.string().nullish(),
  email: z.string().email(),
  phone: z
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/, { message: "Niepoprawny nr telefonu" })
    .nullish(),
  epuapAdress: z.string().nullish(),
});
const createApplicationFormSchema = z.object({
  applicants: stakeholderSchema,
  isOwnerOrPerpetualUsufructuary: z.enum(YES_NO_OPTIONS, {
    required_error: "Wybierz odpowiedź Tak lub Nie",
  }),
  mailingAddress: nullableContacDetailsSchema.nullish(),
  representative: nullableStakeholderSchema.extend({ type: z.enum(STAKEHOLDER_TYPE).nullish() }).nullish(),
  electronicCorrespondenceConsent: z.enum(YES_NO_OPTIONS, {
    required_error: "Wybierz odpowiedź Tak lub Nie",
  }),
  attachmentTypes: z.array(z.enum(ATTACHMENT_TYPES)).default([]),
  files: z.instanceof(FileList).optional(),
  contentOfLetter: z.string().optional(),
  parcels: z.array(
    z.object({
      geometry: z.any(),
      "7point2": z.object({
        1: z.string(),
        2: z.string(),
        3: z.enum(["Tak", "Nie"]),
        4: z.string().min(1, "Treść jest wymagana dla każdej działki"),
      }),
      // 7.3 jest nieobowiązkowe (wzór pisma: "W przypadku wypełnienia,
      // należy uzupełnić każdą z kolumn tabeli") — można pominąć całą
      // sekcję; parametry 7.3.5–7.3.7 bez symbolu 7.3.4 nie mają sensu.
      "7point3": z
        .object({
          4: z.string().optional(),
          5: z.string().optional(),
          6: z.string().optional(),
          7: z.string().optional(),
        })
        .superRefine((v, ctx) => {
          const hasParams = [v[5], v[6], v[7]].some((x) => !!x && x.trim().length > 0);
          if (hasParams && !(v[4] && v[4].trim())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["4"],
              message: "Podając parametry 7.3.5–7.3.7 wybierz też symbol przeznaczenia (7.3.4) albo wyczyść sekcję 7.3",
            });
          }
        })
    })
  ).nullish()
}).refine(
  (data) => {
    const hasContent = !!data.contentOfLetter?.trim();
    const hasParcels = data.parcels && data.parcels.length > 0;
    const allParcelsHaveContent = hasParcels && data.parcels!.every(p => p?.["7point2"]?.["4"]?.trim());
    return hasContent || allParcelsHaveContent;
  },
  {
    message: "Wypełnij treść pisma (7.1) lub dodaj działki z treścią dla każdej (7.2.4)",
    path: ["contentOfLetter"],
  }
);

export type CreateApplicationFormValuesType = z.infer<typeof createApplicationFormSchema>;

export {
  contacDetailsSchema,
  stakeholderSchema,
  createApplicationFormSchema,
  nullableContacDetailsSchema,
};

export type LoginFormValuesType = z.infer<typeof loginFormSchema>;

export const loginFormSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

export type RequestResetFormValuesType = z.infer<typeof requestResetFormSchema>;

export const requestResetFormSchema = z.object({
  email: z.string().email(),
});

export const createTenantFormSchema = nullableContacDetailsSchema.extend({
  id: z.string().regex(TENANT_ID_PATTERN, TENANT_ID_PATTERN_MESSAGE),
  displayName: z.string(),
  adminEmail: z.string().email(),
});
export type CreateTenantFormValuesType = z.infer<typeof createTenantFormSchema>;
export const editTenantFormSchema = nullableContacDetailsSchema.extend({
  id: z
    .string()
    .trim()
    .min(1, "Adres URL jest wymagany")
    .max(100, "Adres URL może mieć maksymalnie 100 znaków")
    .regex(TENANT_ID_PATTERN, TENANT_ID_PATTERN_MESSAGE),
  displayName: z.string().trim().min(1, "Nazwa gminy jest wymagana"),
  // Układ współrzędnych mapy dla tej gminy:
  //  - "PL2000" = PL-2000, strefa wybierana automatycznie po długości geograficznej
  //    (EPSG:2176/2177/2178/2179). Dla większości gmin w centralnej Polsce.
  //  - "PL1992" = PL-1992 / CS92 (EPSG:2180). Jeden układ dla całej Polski,
  //    bez stref. Pokrycie całego kraju w jednej projekcji.
  mapCrs: z.enum(["PL2000", "PL1992"]).default("PL2000").optional(),
  // Kolor motywu publicznej strony gminy (nagłówek + akcenty tekstu).
  // Dowolna wartość HEX (#rgb lub #rrggbb). Przyciski pozostają w szarej
  // palecie niezależnie od tego ustawienia.
  primaryColor: z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, {
      message: "Kolor musi być w formacie HEX, np. #1d4ed8",
    })
    .optional(),
  // Logo/herb gminy. Zapisywany jako data URL (base64) bezpośrednio w payload.
  // Domyślny limit UI ~5 MB (wystarcza dla herbów PNG/SVG). Dla większych
  // plików w przyszłości można przejść na osobny storage + URL zamiast base64.
  logo: z.string().optional(),
});
export type EditTenantFormValuesType = z.infer<typeof editTenantFormSchema>;

export type EditUserFormValuesType = z.infer<typeof stakeholderSchema>;

/**
 * Slugs zarezerwowane — kolidują ze statycznymi sciezkami w routerze pod
 * /$tenant/*. Procedura o takim slugu byloby niedostepna (router wybierze
 * statyczna sciezke). Walidacja uniemozliwia uzycie ich jako URL procedury.
 */
export const RESERVED_PROCEDURE_SLUGS = [
  "login",
  "register",
  "edit",
  "edit-me",
  "procedures",
  "create-procedure",
  "consultation",
  "consultation-admin",
  "consultant-dashboard",
  "manage-consultants",
  "applications",
  "applications-map",
  "dashboard",
  "user",
  "application",
] as const;

export const createProcedureFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Nazwa URL jest wymagana")
    .regex(
      /^[A-Za-z0-9_-]+$/,
      "Tylko litery (bez polskich znaków), cyfry, podkreślenie i myślnik",
    )
    .refine(
      (v) => !RESERVED_PROCEDURE_SLUGS.includes(v.toLowerCase() as any),
      { message: "Ta nazwa jest zarezerwowana dla systemu" },
    ),
  displayName: z.string().trim().min(1, "Nazwa procedury jest wymagana"),
  description: z.string().trim().min(1, "Opis procedury jest wymagany"),
  activeFrom: z.coerce.date(),
  activeTo: z.coerce.date(),
  applicationPlace: z.string().trim().min(1, "Organ, do którego składane jest pismo, jest wymagany"),
  typeOfLetter: z.string().trim().min(1, "Rodzaj pisma jest wymagany"),
  spatialPlanningActType: z.string().trim().min(1, "Rodzaj aktu planowania przestrzennego jest wymagany"),
  files: z.instanceof(FileList).optional(),
  spatialPlanningActName: z.string().trim().min(1, "Nazwa aktu planowania przestrzennego jest wymagana"),
  municipalitysGeneralPlanOrLocalDevelopmentPlan: z
    .string()
    .trim()
    .min(1, "Nazwa planu ogólnego gminy lub MPZP jest wymagana"),
  // Punkt startowy mapy dla tej procedury — opcjonalne.
  // Gdy nie podane, frontend użyje sensownego domyślnego (np. centrum gminy
  // z Nominatim albo wartość z wczesniejszej TenantPublicPage).
  mapInitialLat: z.coerce.number().min(-90).max(90).optional().or(z.literal("")).or(z.nan()),
  mapInitialLng: z.coerce.number().min(-180).max(180).optional().or(z.literal("")).or(z.nan()),
  mapInitialZoom: z.coerce.number().min(0).max(22).optional().or(z.literal("")).or(z.nan()),
  // Domyślna konfiguracja warstw mapy dla tej procedury — używana przy
  // pierwszym wejściu użytkownika na stronę procedury (gdy localStorage
  // per-procedure jest pusty). Po zmianie przez użytkownika lokalny
  // override przechowywany jest w localStorage.
  // Pole ZAPISYWANE jest do payload (JSONB) i NIE walidowane tu ściśle —
  // przechodzi jako opaque record żeby schema nie musiała znać typów
  // LayerId. Backend form.dto.ts też przyjmuje payload as any.
  defaultLayerVisibility: z.record(z.string(), z.boolean()).optional(),
  defaultLayerOrder: z.array(z.string()).optional(),
}).refine(({ activeFrom, activeTo }) => activeTo > activeFrom, {
  message: "Data zakończenia musi być późniejsza niż data rozpoczęcia",
  path: ["activeTo"],
});
export type CreateProcedureFormValuesType = z.infer<typeof createProcedureFormSchema>;
